/**
 * Edge snapping for the room editor's free elements, as if the pieces were
 * physical: an unrotated element, moved or resized by a side, snaps an edge
 * onto another axis-aligned edge only where the two would bump — their
 * segments overlap along the edge — never onto one it could slide past. The
 * edges are those of every other unrotated element, whatever its kind or
 * height (an upper cabinet lines up with the counter under it), and the
 * room's axis-aligned walls. An element against a wall also snaps its start
 * and end along the wall to the openings on it, the way the assembler lines
 * up openings across a shared wall. In plan, X and Y; from the 3D gadget,
 * height too: an element's bottom or top lands on the floor, the ceiling, or
 * the top or bottom of another element whose footprint it overlaps.
 */
import { type FloorPlanFreeElement, type FloorPlanRoom, freeElementSpan, roomCeilingHeight, roomFreeElements } from '@kaiizen/kai-floor-plan';
import { wallRuns } from '../room-geometry';

/** One axis-aligned edge: its line (`at`) and its extent along that line. */
export interface EdgeSegment {
  at: number;
  from: number;
  to: number;
  /** A wall's openings, each as its start and end along the wall. */
  openings?: number[];
}

/** The vertical edges (x = `at`, spanning y) and horizontal edges (y = `at`, spanning x) an element may snap to. */
export interface SnapEdges {
  xs: EdgeSegment[];
  ys: EdgeSegment[];
}

export interface Frame {
  x: number;
  y: number;
  width: number;
  length: number;
}

const OVERLAP_EPSILON = 1e-9;
/** Whether two extents share more than a point. */
const overlaps = (a: EdgeSegment, from: number, to: number): boolean => Math.min(a.to, to) - Math.max(a.from, from) > OVERLAP_EPSILON;

/** Every edge in the room other than those of the element at `except`. */
export function snapEdges(shape: FloorPlanRoom, except: number): SnapEdges {
  const xs: EdgeSegment[] = [], ys: EdgeSegment[] = [];
  roomFreeElements(shape).forEach((element, index) => {
    if (index === except || element.rotation) return;
    const { x, y, width, length } = element;
    xs.push({ at: x, from: y, to: y + length }, { at: x + width, from: y, to: y + length });
    ys.push({ at: y, from: x, to: x + width }, { at: y + length, from: x, to: x + width });
  });
  for (const run of wallRuns(shape)) {
    const { innerStart, line: { dir } } = run.first;
    const vertical = Math.abs(dir.x) < 1e-9, horizontal = Math.abs(dir.y) < 1e-9;
    if (!vertical && !horizontal) continue;
    const along = (s: number): number => vertical ? innerStart.y + dir.y * s : innerStart.x + dir.x * s;
    const ends = [along(0), along(run.length)];
    const segment: EdgeSegment = {
      at: vertical ? innerStart.x : innerStart.y,
      from: Math.min(...ends),
      to: Math.max(...ends),
      openings: run.openings.map(along),
    };
    (vertical ? xs : ys).push(segment);
  }
  return { xs, ys };
}

/** The smallest of the offsets, or null when there are none. */
const closest = (offsets: readonly number[]): number | null => offsets.length ? offsets.reduce((best, d) => Math.abs(d) < Math.abs(best) ? d : best) : null;

/** The line nearest `value` within reach among plain coordinates, as the offset to it; null when none. */
export function nearestOffset(value: number, lines: readonly number[], reach: number): number | null {
  return closest(lines.map(line => line - value).filter(d => Math.abs(d) <= reach));
}

/**
 * The offset that brings whichever of two parallel edges (`low`, `high`, both
 * spanning [`from`, `to`]) is nearer a segment it overlaps onto it; null
 * when neither is within reach of one.
 */
export function snapSpan(low: number, high: number, from: number, to: number, segments: readonly EdgeSegment[], reach: number): number | null {
  const lines = segments.filter(segment => overlaps(segment, from, to)).map(segment => segment.at);
  return closest([low, high].map(edge => nearestOffset(edge, lines, reach)).filter((d): d is number => d !== null));
}

/**
 * The openings along the walls a frame is against: the start and end of each
 * as coordinates on the axis along that wall — x for a wall above or below the
 * frame, y for one beside it.
 */
export function touchedOpenings(frame: Frame, edges: SnapEdges): { xs: number[]; ys: number[] } {
  const against = (segment: EdgeSegment, edge0: number, edge1: number, from: number, to: number): boolean =>
    (Math.abs(segment.at - edge0) < 1e-6 || Math.abs(segment.at - edge1) < 1e-6) && overlaps(segment, from, to);
  return {
    xs: edges.ys.filter(wall => wall.openings && against(wall, frame.y, frame.y + frame.length, frame.x, frame.x + frame.width)).flatMap(wall =>
      wall.openings!
    ),
    ys: edges.xs.filter(wall => wall.openings && against(wall, frame.x, frame.x + frame.width, frame.y, frame.y + frame.length)).flatMap(wall =>
      wall.openings!
    ),
  };
}

/** A frame's x or y edges snapped: the nearer edge onto an overlapping segment, or onto a plain coordinate (an opening's edge). */
function snapAxis(frame: Frame, axis: 'x' | 'y', edges: SnapEdges, openings: readonly number[], reach: number): number | null {
  const [low, high, from, to, segments] = axis === 'x'
    ? [frame.x, frame.x + frame.width, frame.y, frame.y + frame.length, edges.xs]
    : [frame.y, frame.y + frame.length, frame.x, frame.x + frame.width, edges.ys];
  return closest(
    [snapSpan(low, high, from, to, segments, reach), ...[low, high].map(edge => nearestOffset(edge, openings, reach))]
      .filter((d): d is number => d !== null),
  );
}

/**
 * A moved element's frame snapped: first the closer of its two axes onto an
 * overlapping edge, then the other axis onto an overlapping edge or, once it
 * is against a wall, onto an opening's start or end along that wall.
 */
export function snapFrame(frame: Frame, edges: SnapEdges, reach: number): { x: number; y: number } {
  const dx = snapAxis(frame, 'x', edges, [], reach), dy = snapAxis(frame, 'y', edges, [], reach);
  if (dx === null && dy === null) return { x: frame.x, y: frame.y };
  const xFirst = dy === null || (dx !== null && Math.abs(dx) <= Math.abs(dy));
  const snapped: Frame = xFirst ? { ...frame, x: frame.x + dx! } : { ...frame, y: frame.y + dy! };
  const openings = touchedOpenings(snapped, edges);
  const second = xFirst ? snapAxis(snapped, 'y', edges, openings.ys, reach) : snapAxis(snapped, 'x', edges, openings.xs, reach);
  return xFirst ? { x: snapped.x, y: snapped.y + (second ?? 0) } : { x: snapped.x + (second ?? 0), y: snapped.y };
}

/**
 * A side-resize's size snapped: the moving side, at `fixed + outward × size`
 * and spanning [`from`, `to`], lands on an overlapping segment or, with the
 * element against a wall, on an opening's edge along it; the opposite side
 * stays put.
 */
export function snapSideSize(
  size: number,
  fixed: number,
  outward: 1 | -1,
  from: number,
  to: number,
  segments: readonly EdgeSegment[],
  openings: readonly number[],
  reach: number,
): number {
  const edge = fixed + outward * size;
  const lines = [...segments.filter(segment => overlaps(segment, from, to)).map(segment => segment.at), ...openings];
  const offset = nearestOffset(edge, lines, reach);
  return offset === null ? size : size + outward * offset;
}

export { DEFAULT_OBJECT_HEIGHT, freeElementSpan } from '@kaiizen/kai-floor-plan';

/**
 * Whether two footprints overlap: exactly for two unrotated rectangles, and
 * for a rotated one by whether the circles inside the two rectangles meet, a
 * cheap test that never claims an overlap that is not there.
 */
export function footprintsOverlap(a: FloorPlanFreeElement, b: FloorPlanFreeElement): boolean {
  if (!a.rotation && !b.rotation) {
    return Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x) > OVERLAP_EPSILON
      && Math.min(a.y + a.length, b.y + b.length) - Math.max(a.y, b.y) > OVERLAP_EPSILON;
  }
  const distance = Math.hypot(a.x + a.width / 2 - b.x - b.width / 2, a.y + a.length / 2 - b.y - b.length / 2);
  return distance < Math.min(a.width, a.length) / 2 + Math.min(b.width, b.length) / 2;
}

/**
 * An element's base snapped in height: its bottom or top, whichever is
 * nearer, lands on the floor, the ceiling (when the room has one), or the
 * top or bottom of another element whose footprint overlaps its own.
 */
export function snapBase(shape: FloorPlanRoom, index: number, base: number, height: number, reach: number): number {
  const elements = roomFreeElements(shape);
  const me = elements[index];
  if (!me) return base;
  const ceiling = roomCeilingHeight(shape);
  const planes: number[] = ceiling > 0 ? [0, ceiling] : [0];
  elements.forEach((other, i) => {
    if (i === index || !footprintsOverlap(me, other)) return;
    planes.push(...freeElementSpan(other, ceiling));
  });
  const offset = closest([base, base + height].map(edge => nearestOffset(edge, planes, reach)).filter((d): d is number => d !== null));
  return offset === null ? base : base + offset;
}
